import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { Subscription } from '../billing/Subscriptions';

const money = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

interface Device {
  deviceId: string;
  serialNumber: string;
  name?: string;
  restricted: boolean;
  boundLicenseId?: string;
  lastUsed?: string;
  retiredUntil?: string;
}

interface Quote {
  amountDue: number;
  currency: string;
  days: number;
  periodEnd: number | null;
  additional: number;
  current: number;
}

interface PurchaseResult {
  additional: number;
  timing?: string;
  charged?: {
    invoiceId: string; amountPaid: number; amountDue: number; currency: string; status: string; hostedUrl: string | null;
  };
}

interface License {
  licenseId: string;
  status: string;
  deviceId: string | null;
  cooldownUntil: string | null;
  subscriptionId: string;
}

export function Devices() {
  const qc = useQueryClient();
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => api.get<Device[]>('/devices') });
  const licenses = useQuery({ queryKey: ['licenses'], queryFn: () => api.get<License[]>('/licenses') });
  const subs = useQuery({ queryKey: ['subscriptions'], queryFn: () => api.get<Subscription[]>('/subscriptions') });
  const [err, setErr] = useState<string | null>(null);
  const [confirmBuy, setConfirmBuy] = useState(false);
  /** What the last purchase actually did to the customer's bill. */
  const [purchase, setPurchase] = useState<PurchaseResult | null>(null);
  const [replaceFor, setReplaceFor] = useState<{ device: Device; licenseId: string } | null>(null);
  const [replaceTarget, setReplaceTarget] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['devices'] });
    qc.invalidateQueries({ queryKey: ['licenses'] });
  };

  /*
   * Whether device licences mean anything to this customer at all. On a pay-per-transaction plan any
   * scanner may capture and no seat is ever needed, so labelling their devices "Unlicensed" with a dead
   * "No seats" button described a problem they did not have — the report that brought this in was a
   * migrated customer reading exactly that as "I have no licence for my device".
   */
  const hasLicensedPlan = useMemo(
    () => (subs.data ?? []).some((x) => x.status === 'ACTIVE' && x.billingMode === 'licensed'),
    [subs.data],
  );

  const available = useMemo(
    () => (licenses.data ?? []).filter((l) => l.status === 'AVAILABLE'),
    [licenses.data],
  );
  const licenseByDevice = useMemo(() => {
    const m = new Map<string, License>();
    for (const l of licenses.data ?? []) if (l.deviceId && l.status === 'ALLOCATED') m.set(l.deviceId, l);
    return m;
  }, [licenses.data]);
  // A seat freed from a scanner can be re-attached to that SAME scanner any time (no cooldown wait).
  const reattachByDevice = useMemo(() => {
    const m = new Map<string, License>();
    for (const l of licenses.data ?? []) if (l.deviceId && l.status === 'DEALLOCATING') m.set(l.deviceId, l);
    return m;
  }, [licenses.data]);

  const allocate = useMutation({
    mutationFn: (v: { licenseId: string; deviceId: string }) => api.post('/licenses/allocate', v),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Allocation failed'),
  });
  const deallocate = useMutation({
    mutationFn: (v: { licenseId: string }) => api.post('/licenses/deallocate', v),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Deallocation failed'),
  });
  const restrict = useMutation({
    mutationFn: (v: { deviceId: string; restricted: boolean }) => api.patch(`/devices/${v.deviceId}`, { restricted: v.restricted }),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Update failed'),
  });
  const replace = useMutation({
    mutationFn: (v: { licenseId: string; newDeviceId: string }) => api.post('/licenses/replace', v),
    onSuccess: () => { refresh(); setReplaceFor(null); setReplaceTarget(''); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Replace failed'),
  });

  const total = licenses.data?.length ?? 0;
  const allocated = (licenses.data ?? []).filter((l) => l.status === 'ALLOCATED').length;
  /*
   * Seats are not just allocated-or-available: a freed seat sits in DEALLOCATING for a 30-day cooldown,
   * and a seat can be EXPIRED or PAST_DUE. Counting only the first two made "2/3 allocated · 0 available"
   * look like a contradiction — the third seat existed but nothing on screen said where it had gone.
   */
  const cooling = (licenses.data ?? []).filter((l) => l.status === 'DEALLOCATING');
  const unusable = (licenses.data ?? []).filter((l) => l.status === 'EXPIRED' || l.status === 'PAST_DUE');
  /** Soonest moment a cooling seat becomes AVAILABLE again, for the explanation below. */
  const nextFree = cooling
    .map((l) => l.cooldownUntil)
    .filter((d): d is string => Boolean(d))
    .sort()[0];
  /*
   * Seats the customer actually holds. An EXPIRED or PAST_DUE seat is not one of them, so counting it in
   * the denominator read as "you have 3 seats" while only 2 could ever be used — the reported confusion.
   * A DEALLOCATING seat still counts: it is owned, just cooling down.
   */
  const entitled = total - unusable.length;
  /*
   * Additional seats beyond the one the plan includes. The quote is an ABSOLUTE target, so it has to be
   * derived from what the customer holds rather than sent as "one more" — asking for "1" when they already
   * have three would read as a reduction.
   */
  const additionalNow = Math.max(0, entitled - 1);

  /*
   * What one more scanner costs right now, asked before the customer agrees to it.
   *
   * Quoted by Stripe rather than computed here, so the number in the dialog is the number on the invoice.
   * Fetched only while the dialog is open — it is a live figure that changes as the period elapses, and a
   * stale one is worse than none.
   */
  const quote = useQuery({
    queryKey: ['scanner-quote', (licenses.data ?? []).length],
    queryFn: () => api.get<Quote>(`/licenses/scanners/quote?additional=${additionalNow + 1}`),
    enabled: confirmBuy,
    staleTime: 0,
  });

  const buySeat = useMutation({
    mutationFn: () => api.post<PurchaseResult>('/licenses/purchase', { quantity: 1 }),
    onSuccess: (r) => { setErr(null); setPurchase(r); refresh(); },
    onError: (e) => {
      if (e instanceof ApiError && (e.body as { code?: string })?.code === 'NO_LICENSED_PLAN') {
        setErr('You need an active per-device (unlimited) plan before buying seats — set one up on the Subscriptions page.');
      } else {
        setErr(e instanceof ApiError ? e.message : 'Could not purchase a seat');
      }
    },
  });

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Devices &amp; licenses</h1>
          <div className="sub">
            {hasLicensedPlan
              ? 'Register scanners and allocate per-device unlimited licenses. Each licensed device runs unlimited EFTs.'
              : 'Scanners you have used. On a pay-per-transaction plan any scanner can capture — no licenses to allocate.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/* Buying a seat without a per-device plan is rejected server-side (NO_LICENSED_PLAN); don't
              offer the action at all rather than explain the refusal afterwards. */}
          {hasLicensedPlan && (
            <button className="btn btn-primary" onClick={() => { setErr(null); setConfirmBuy(true); }} disabled={buySeat.isPending}>
              {buySeat.isPending ? 'Purchasing…' : '+ Buy device seat'}
            </button>
          )}
          <RegisterDevice onDone={refresh} onError={setErr} />
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-chip"><span className="stat-num">{devices.data?.length ?? 0}</span><span className="stat-lbl">Devices</span></div>
        {(hasLicensedPlan || total > 0) && (
          <>
            <div className="stat-chip"><span className="stat-num">{allocated}/{entitled}</span><span className="stat-lbl">Licenses allocated</span></div>
            <div className="stat-chip"><span className="stat-num">{available.length}</span><span className="stat-lbl">Available</span></div>
          </>
        )}
        {cooling.length > 0 && (
          <div className="stat-chip"><span className="stat-num">{cooling.length}</span><span className="stat-lbl">In cooldown</span></div>
        )}
        {unusable.length > 0 && (
          <div className="stat-chip"><span className="stat-num">{unusable.length}</span><span className="stat-lbl">Expired / past due</span></div>
        )}
      </div>

      {err && <p className="cart-error">{err}</p>}

      {/* What the charge actually did. A declined card leaves the seat added and the invoice open, which the
          customer has to be told plainly rather than left to discover when the plan lapses. */}
      {purchase?.charged && (
        purchase.charged.amountDue > 0 ? (
          <div className="banner-warn">
            Seat added, but the {money(purchase.charged.amountDue, purchase.charged.currency)} charge for it hasn't
            been paid ({purchase.charged.status}).{' '}
            {purchase.charged.hostedUrl && (
              <a className="track-link" href={purchase.charged.hostedUrl} target="_blank" rel="noreferrer">Pay it now ↗</a>
            )}
          </div>
        ) : (
          <div className="banner-ok">
            Seat added and {money(purchase.charged.amountPaid, purchase.charged.currency)} charged for the rest of this
            billing period.
          </div>
        )
      )}

      {devices.isLoading ? (
        <p className="loading-screen">// loading devices…</p>
      ) : (devices.data ?? []).length === 0 ? (
        <div className="panel empty">
          <h3>No devices registered</h3>
          <p>
            {hasLicensedPlan
              ? 'Register a scanner by its serial number to allocate a license.'
              : 'A scanner registers itself the first time you capture with it. You can also add one by serial number.'}
          </p>
        </div>
      ) : (
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="tx-table">
            <thead><tr><th>Device</th><th>Serial</th><th>License</th><th>Status</th><th>Last used</th><th></th></tr></thead>
            <tbody>
              {devices.data!.map((d) => {
                const lic = licenseByDevice.get(d.deviceId);
                return (
                  <tr key={d.deviceId}>
                    <td>{d.name ?? '—'}</td>
                    <td className="mono">{d.serialNumber}</td>
                    {/* An allocated seat always reads as Licensed, even if the plan behind it has since
                        lapsed — the row is the fact. Only the absence changes meaning by plan. */}
                    <td>
                      {lic ? <span className="status-dot ok">Licensed</span>
                        : hasLicensedPlan ? <span className="status-dot muted">Unlicensed</span>
                        : <span className="status-dot ok" title="Pay-per-transaction plans need no device license — any scanner can capture.">Per transaction</span>}
                    </td>
                    <td>
                      {d.retiredUntil && new Date(d.retiredUntil) > new Date()
                        ? <span className="status-dot warn" title={`Retired until ${new Date(d.retiredUntil).toLocaleString()}`}>Retired</span>
                        : d.restricted ? <span className="status-dot warn">Restricted</span> : <span className="status-dot ok">OK</span>}
                    </td>
                    <td>{d.lastUsed ? new Date(d.lastUsed).toLocaleDateString() : '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {lic ? (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => { setErr(null); deallocate.mutate({ licenseId: lic.licenseId }); }} disabled={deallocate.isPending}>
                            Deallocate
                          </button>{' '}
                          <button className="btn btn-ghost btn-sm" title="Broken scanner? Swap this license to a new one immediately."
                            onClick={() => { setErr(null); setReplaceTarget(''); setReplaceFor({ device: d, licenseId: lic.licenseId }); }}>
                            Replace
                          </button>
                        </>
                      ) : reattachByDevice.get(d.deviceId) ? (
                        <button className="btn btn-primary btn-sm" disabled={d.restricted || allocate.isPending}
                          title="This seat was freed from this scanner — re-attach it any time."
                          onClick={() => { setErr(null); allocate.mutate({ licenseId: reattachByDevice.get(d.deviceId)!.licenseId, deviceId: d.deviceId }); }}>
                          Re-attach license
                        </button>
                      ) : hasLicensedPlan ? (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={available.length === 0 || d.restricted || allocate.isPending}
                          onClick={() => { setErr(null); allocate.mutate({ licenseId: available[0]!.licenseId, deviceId: d.deviceId }); }}
                        >
                          {available.length === 0 ? 'No seats' : 'Allocate license'}
                        </button>
                      ) : null}
                      {' '}
                      <button className="btn btn-ghost btn-sm" onClick={() => { setErr(null); restrict.mutate({ deviceId: d.deviceId, restricted: !d.restricted }); }} disabled={restrict.isPending}>
                        {d.restricted ? 'Unrestrict' : 'Restrict'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Previously only fired when total === allocated, so the "0 available with a seat unaccounted for"
          case — the one that looks like a bug — explained nothing. */}
      {available.length === 0 && total > 0 && (
        <p className="cart-note" style={{ textAlign: 'left', marginTop: 16 }}>
          {cooling.length > 0 ? (
            <>
              // No seats are free to assign. {cooling.length === 1 ? 'One seat is' : `${cooling.length} seats are`}{' '}
              in the 30-day cooldown that follows freeing a seat from a scanner — it can be re-attached to the
              <strong> same</strong> scanner right away, and becomes assignable to a different one
              {nextFree ? <> on {new Date(nextFree).toLocaleDateString()}</> : ' once the cooldown ends'}. Need a
              seat now? Use <strong>Replace</strong> on the scanner it came from, or buy another on the{' '}
              <a href="/app/subscriptions">Subscriptions</a> page.
            </>
          ) : unusable.length > 0 ? (
            <>
              // No seats are free to assign — {unusable.length === 1 ? 'one seat is' : `${unusable.length} seats are`}{' '}
              expired or past due. Check the subscription on the <a href="/app/subscriptions">Subscriptions</a> page.
            </>
          ) : (
            <>
              // All license seats are allocated. Buy more seats on the <a href="/app/subscriptions">Subscriptions</a> page.
            </>
          )}
        </p>
      )}
      {total === 0 && !hasLicensedPlan && (
        <p className="cart-note" style={{ textAlign: 'left', marginTop: 16 }}>
          // Device licenses apply only to per-device unlimited plans. On a pay-per-transaction plan, any device can
          capture — no licenses needed. To license specific devices for unlimited use, choose a per-device plan on the{' '}
          <a href="/app/subscriptions">Subscriptions</a> page.
        </p>
      )}

      <ConfirmDialog
        open={confirmBuy}
        title="Buy a device seat?"
        confirmLabel="Buy seat"
        busy={buySeat.isPending}
        onCancel={() => setConfirmBuy(false)}
        onConfirm={() => { buySeat.mutate(); setConfirmBuy(false); }}
      >
        <p>This adds one additional scanner license to your per-device plan.</p>
        {/* The figure comes from Stripe's own preview of this exact change, so what is agreed to here is
            what gets invoiced. */}
        {quote.isLoading ? (
          <p className="field-help">Working out what you'll be charged…</p>
        ) : quote.data && quote.data.amountDue > 0 ? (
          <p className="banner-ok" style={{ margin: '0 0 12px' }}>
            <strong>{money(quote.data.amountDue, quote.data.currency)} charged now</strong> — covering the{' '}
            {quote.data.days} day{quote.data.days === 1 ? '' : 's'} left in this billing period
            {quote.data.periodEnd ? <> (through {new Date(quote.data.periodEnd * 1000).toLocaleDateString()})</> : null}.
            It then renews at the full additional-scanner rate.
          </p>
        ) : (
          <p className="field-help">
            Charged at your plan's additional-scanner rate, prorated for the time left in this billing period.
          </p>
        )}
        <p className="confirm-policy">
          <strong>Cancellation policy:</strong> you can remove a scanner any time from the Subscriptions page, but
          removals take effect at the <strong>end of the billing period</strong> — <strong>no mid-cycle credit or
          refund</strong> — and you can't reduce below the number of scanners currently assigned to a device.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={replaceFor != null}
        title="Replace a broken scanner"
        confirmLabel="Replace scanner"
        busy={replace.isPending}
        confirmDisabled={!replaceTarget}
        onCancel={() => { setReplaceFor(null); setReplaceTarget(''); }}
        onConfirm={() => { if (replaceFor && replaceTarget) replace.mutate({ licenseId: replaceFor.licenseId, newDeviceId: replaceTarget }); }}
      >
        {replaceFor && (() => {
          const eligible = (devices.data ?? []).filter(
            (t) => t.deviceId !== replaceFor.device.deviceId && !licenseByDevice.has(t.deviceId) && !t.restricted,
          );
          return (
            <>
              <p>
                Move this license from <strong>{replaceFor.device.name ?? replaceFor.device.serialNumber}</strong> to a
                replacement scanner. Takes effect <strong>immediately, at no extra charge</strong>.
              </p>
              {eligible.length === 0 ? (
                <p className="confirm-policy">
                  No eligible replacement scanner is registered. Register the new scanner first (use <strong>＋ Register
                  device</strong>), then Replace.
                </p>
              ) : (
                <label className="field">
                  <span className="field-label">Replacement scanner</span>
                  <select className="select" value={replaceTarget} onChange={(e) => setReplaceTarget(e.target.value)}>
                    <option value="">Select a registered scanner…</option>
                    {eligible.map((t) => (
                      <option key={t.deviceId} value={t.deviceId}>{t.name ? `${t.name} · ` : ''}{t.serialNumber}</option>
                    ))}
                  </select>
                </label>
              )}
              <p className="confirm-policy">
                The broken scanner is <strong>retired</strong> — it can't hold a license again for one billing cycle,
                and the swap is logged for support. Use this only for a genuinely failed unit.
              </p>
            </>
          );
        })()}
      </ConfirmDialog>
    </div>
  );
}

function RegisterDevice({ onDone, onError }: { onDone: () => void; onError: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); onError('');
    try {
      await api.post('/devices', { serialNumber: serial.trim(), ...(name.trim() ? { name: name.trim() } : {}) });
      setSerial(''); setName(''); setOpen(false); onDone();
    } catch (e2) {
      onError(e2 instanceof ApiError ? e2.message : 'Could not register device');
    } finally { setBusy(false); }
  }

  if (!open) return <button className="btn btn-primary" onClick={() => setOpen(true)}>+ Register device</button>;
  return (
    <form className="inline-form" onSubmit={submit}>
      <input className="input" placeholder="Scanner serial number" value={serial} onChange={(e) => setSerial(e.target.value)} required />
      <input className="input" placeholder="Nickname (optional)" value={name} onChange={(e) => setName(e.target.value)} />
      <button className="btn btn-primary" type="submit" disabled={busy || !serial.trim()}>{busy ? 'Adding…' : 'Add'}</button>
      <button className="btn btn-ghost" type="button" onClick={() => setOpen(false)}>Cancel</button>
    </form>
  );
}
